import type { PageProps } from '@apps/card/routes/CardCustomizer/components/Page';
import { Page } from '@apps/card/routes/CardCustomizer/components/Page';
import { PageScaler } from '@apps/card/routes/CardCustomizer/components/PageScaler';
import { usePointerUnitPosition } from '@apps/card/routes/CardCustomizer/components/usePointerUnitPosition';
import { PAPER_TEXTURE_URL } from '@shared/components/GiftWrap/Reveal/premiumEnvelope.styles';
import { StationeryDraftFormat } from '@graphql/generated';
import { Box } from '@withjoy/joykit';
import React from 'react';
import { addEcardUtmParams, isJoyWebsiteUrl } from '@shared/utils/ecard/utm.ecard';
import { ensureHttpsPrefix } from '@shared/utils/urlHelpers';

interface InteractiveCardProps extends Pick<PageProps, 'width' | 'height' | 'page' | 'shape' | 'format'> {
  interactive?: boolean;
  link?: string;
  onLinkClick?: () => void;
}

export const InteractiveCard: React.FC<InteractiveCardProps> = props => {
  const { width, height, page, shape, interactive = true, link, onLinkClick } = props;
  const { unitPosition, elementRef } = usePointerUnitPosition();
  const { x, y } = interactive ? unitPosition : { x: 0, y: 0 };

  const handleOpenLink = () => {
    if (!link) return;
    function normalizeLink(link: string) {
      // Approximate email regex. A fully correct regex gets complicated.
      if (/^\S+@\S+$/.test(link)) return `mailto:${link}`;
      return ensureHttpsPrefix(link);
    }
    onLinkClick?.();
    const normalizedLink = normalizeLink(link.trim());
    const finalUrl = isJoyWebsiteUrl(normalizedLink) ? addEcardUtmParams(normalizedLink, { ecardType: 'premium' }) : normalizedLink;

    window.open(finalUrl, '_blank');
  };

  return (
    <Box
      ref={elementRef}
      style={{
        width,
        height,
        transform: `perspective(504px) rotateX( ${-y * 2}deg) rotateY(${x * 2}deg)`,
        filter: `drop-shadow(${-x * 20 - 10}px ${-y * 20 + 15}px  15px rgba(0,0, 0, 0.35))`,
        cursor: link ? 'pointer' : 'default',
      }}
      onClick={handleOpenLink}
      onKeyPress={handleOpenLink}
      role={link ? 'button' : undefined}
    >
      <PageScaler>
        <Page
          width={5}
          height={7}
          page={page}
          shape={shape}
          format={StationeryDraftFormat.digital}
          texture={`
            url(${PAPER_TEXTURE_URL}),
            radial-gradient(
              ellipse at ${(1 - (x + 0.5)) * 100}% ${(1 - (y + 0.5)) * 100}%,
              hsla(0, 0%, 100%, 0.2),
              hsla(0, 0%, 100%, 0.0)
            )
          `}
        />
      </PageScaler>
    </Box>
  );
};
